
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compare } from './ui/compare';
import { generateModelImage, analyzeUserImageForAttributes } from '../services/geminiService';
import Spinner from './Spinner';
import { getFriendlyErrorMessage } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile, AppPage } from '../types';
import { getUserProfile, checkGenerationQuota, incrementGenerationCount } from '@shared/services/userService';
import { MEMBERSHIP_LIMITS } from '../constants';
import PortfolioCarousel from './PortfolioCarousel';
import { blogPosts } from '@shared/data/blogData';

interface ModelGenerationScreenProps {
  userImageFile: File;
  onModelFinalized: (modelUrl: string, detectedProfile?: Partial<UserProfile>) => void;
  onCancel: () => void;
  isDarkMode: boolean;
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
  qualityPreference?: 'low' | 'medium' | 'high';
  currentUserId?: string; // Add currentUserId prop
}

const ModelGenerationScreen: React.FC<ModelGenerationScreenProps> = ({ userImageFile, onModelFinalized, onCancel, isDarkMode, onNavigate, qualityPreference, currentUserId }) => {
  const { t, lang } = useTranslation();
  const [userImageUrl, setUserImageUrl] = useState<string | null>(null);
  const [generatedModelUrl, setGeneratedModelUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDetectingAttributes, setIsDetectingAttributes] = useState(false);
  const [detectedAttributes, setDetectedAttributes] = useState<Partial<UserProfile> | null>(null);

  useEffect(() => {
    if (userImageFile) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        setUserImageUrl(dataUrl);

        // --- QUOTA CHECK ---
        const profile = currentUserId ? getUserProfile(currentUserId) : { membershipLevel: 'basic', generationCount: 0 } as UserProfile;
        if (!checkGenerationQuota(profile, currentUserId)) {
          const isGuest = currentUserId?.includes('guest');
          const level = (profile.membershipLevel || 'basic') as keyof typeof MEMBERSHIP_LIMITS;
          const limit = isGuest ? 1 : MEMBERSHIP_LIMITS[level];
          setError(t('error_quota_exceeded', { limit: String(limit) }));
          return;
        }
        // -------------------

        setIsGenerating(true);
        setIsDetectingAttributes(true);
        setGeneratedModelUrl(null);
        setDetectedAttributes(null);
        setError(null);
        try {
          // Run model generation and attribute detection in parallel
          // Pass quality preference to generation function
          const modelPromise = generateModelImage(userImageFile, qualityPreference);
          const attributesPromise = analyzeUserImageForAttributes(userImageFile).catch(err => {
            console.warn("Could not detect user attributes:", err);
            return null; // Don't fail the whole process if this part fails
          });

          const [modelResult, attributesResult] = await Promise.all([modelPromise, attributesPromise]);

          setGeneratedModelUrl(modelResult);
          if (attributesResult) {
            setDetectedAttributes(attributesResult);
          }

          // --- INCREMENT QUOTA ---
          if (currentUserId) {
            incrementGenerationCount(currentUserId);
          }
          // ----------------------

        } catch (err) {
          let friendlyError = getFriendlyErrorMessage(err as unknown, t('startScreen_generationFailed'), lang, t);
          if (err instanceof Error && err.message.includes("safety policies")) {
            friendlyError = t('imageGenError_safety', { details: err.message });
          }
          setError(friendlyError);
        } finally {
          setIsGenerating(false);
          setIsDetectingAttributes(false);
        }
      };
      reader.readAsDataURL(userImageFile);
    }
  }, [userImageFile, lang, t, qualityPreference, currentUserId]);

  const screenVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  const getLoadingText = () => {
    if (isGenerating && isDetectingAttributes) {
      return t('startScreen_loading_both');
    }
    if (isGenerating) {
      return t('startScreen_generatingModel');
    }
    if (isDetectingAttributes) {
      return t('startScreen_detectingAttributes');
    }
    return '';
  };
  const loadingText = getLoadingText();

  return (
    <motion.div
      key="compare"
      className="w-screen min-h-screen flex items-start sm:items-center justify-center bg-gray-50 dark:bg-gray-900 p-4 pb-20"
      variants={screenVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <div className="w-full max-w-6xl mx-auto h-full flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
        <div className="md:w-1/2 flex-shrink-0 flex flex-col items-center md:items-start">
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 leading-tight dark:text-white">
              {t('startScreen_imageTransformTitle', 'Your AI Personal Styling Studio')}
            </h1>
            <p className="mt-2 text-md text-gray-600 dark:text-gray-300">
              {t('startScreen_imageTransformDescription', 'Experience fashion that fits your life, not just your body. Discover confidence through personalized style matching and lifestyle visualization.')}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                ✨ {t('usp_confidence', 'Confidence Boost')}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                🌆 {t('usp_lifestyle', 'Lifestyle Context')}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200">
                👗 {t('usp_styling', 'Personal Stylist')}
              </span>
            </div>
          </div>

          {(isGenerating || isDetectingAttributes) && (
            <div className="flex items-center gap-3 text-lg text-gray-700 font-serif mt-6 dark:text-gray-300">
              <Spinner />
              <span>{loadingText}</span>
            </div>
          )}

          {error &&
            <div className="text-center md:text-left text-red-600 max-w-md mt-6 dark:text-red-300">
              <p className="font-semibold">{t('startScreen_generationFailed')}</p>
              <p className="text-sm mb-4">{error}</p>
              <button onClick={onCancel} className="text-sm font-semibold text-gray-700 hover:underline dark:text-gray-300 dark:hover:text-gray-100">{t('startScreen_tryAgain')}</button>
            </div>
          }

          <AnimatePresence>
            {generatedModelUrl && !isGenerating && !error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col sm:flex-row items-center gap-4 mt-8"
              >
                <button
                  onClick={onCancel}
                  className="w-full sm:w-auto px-6 py-3 text-base font-semibold text-gray-700 bg-gray-200 rounded-md cursor-pointer hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  {t('modelGeneration_cancel')}
                </button>
                <button
                  onClick={() => onModelFinalized(generatedModelUrl as string, detectedAttributes || undefined)}
                  className="w-full sm:w-auto relative inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-gray-900 rounded-md cursor-pointer group hover:bg-gray-700 transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-500"
                >
                  {t('nav_proceedToStyling')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="md:w-1/2 w-full flex items-center justify-center">
          <div
            id="onboarding-compare-slider"
            className={`relative rounded-[1.25rem] transition-all duration-700 ease-in-out w-full max-w-[400px] aspect-[3/4] shadow-2xl ${isGenerating ? 'border border-gray-300 dark:border-gray-600 animate-pulse' : 'border border-transparent'}`}
          >
            <Compare
              firstImage={userImageUrl || undefined}
              secondImage={(generatedModelUrl ?? userImageUrl) || undefined}
              slideMode="drag"
              initialSliderPercentage={50}
              className="w-full h-full rounded-2xl bg-gray-200 dark:bg-gray-700 object-cover"
              particleColor={isDarkMode ? "#E5E7EB" : "#FFFFFF"}
              layoutId="generated-model-image"
            />
          </div>
        </div>
      </div>

      {/* Portfolio Carousel Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="w-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800"
      >
        <PortfolioCarousel
          posts={blogPosts.slice(0, 5)}
          onNavigate={onNavigate}
        />
      </motion.div>
    </motion.div>
  );
};

export default ModelGenerationScreen;
